import React, { useState, useCallback, useEffect } from 'react';
import { Modal, TextInput, Textarea, Button, Stack, Group, Switch } from '@mantine/core';
import { IconDeviceFloppy, IconX } from '@tabler/icons-react';
import { RegionDetail, UpdateRegionCommand } from '@/domain/entities/Region';
import { useUpdateRegionMutation } from '@/application/queries/regionQueries';
import { useAuth } from '../../../hooks/useAuth';

export interface EditRegionModalProps {
  isOpen: boolean;
  onClose: () => void;
  region: RegionDetail;
  onSuccess?: () => void;
}

/**
 * EditRegionModal - Modal for editing region information
 *
 * Features:
 * - Pre-filled form with current region values
 * - Update region name, location, description, and active status
 * - Form validation (required fields, max lengths)
 * - Success/error notifications via mutation hook
 * - Loading state during save
 *
 * Note: Subreddits and SubAreas are managed separately
 */
export const EditRegionModal: React.FC<EditRegionModalProps> = ({
  isOpen,
  onClose,
  region,
  onSuccess
}): React.ReactElement => {
  const { user } = useAuth();
  const updateRegionMutation = useUpdateRegionMutation();

  // Form state
  const [formData, setFormData] = useState<UpdateRegionCommand>({
    regionName: region.name,
    country: region.country,
    state: region.state ?? undefined,
    city: region.city ?? undefined,
    description: region.description ?? undefined,
    isActive: region.isActive
  });

  // Form validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  /**
   * Reset form when modal opens or region changes
   */
  useEffect(() => {
    if (isOpen) {
      setFormData({
        regionName: region.name,
        country: region.country,
        state: region.state ?? undefined,
        city: region.city ?? undefined,
        description: region.description ?? undefined,
        isActive: region.isActive
      });
      setErrors({});
    }
  }, [isOpen, region]);

  /**
   * Handle form field changes
   */
  const handleChange = useCallback((field: keyof UpdateRegionCommand, value: string | boolean): void => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  /**
   * Validate form
   */
  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    // Region name is required
    if (!formData['regionName'] || formData['regionName'].trim() === '') {
      newErrors['regionName'] = 'Region name is required';
    } else if (formData['regionName'].length > 100) {
      newErrors['regionName'] = 'Region name must be 100 characters or less';
    }

    // Country is required
    if (!formData['country'] || formData['country'].trim() === '') {
      newErrors['country'] = 'Country is required';
    } else if (formData['country'].length > 100) {
      newErrors['country'] = 'Country must be 100 characters or less';
    }

    // Optional fields max length validation
    if (formData['state'] && formData['state'].length > 100) {
      newErrors['state'] = 'State must be 100 characters or less';
    }

    if (formData['city'] && formData['city'].length > 100) {
      newErrors['city'] = 'City must be 100 characters or less';
    }

    if (formData['description'] && formData['description'].length > 500) {
      newErrors['description'] = 'Description must be 500 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!validate()) {
      return;
    }

    try {
      await updateRegionMutation.mutateAsync({
        id: region.id,
        command: formData,
        userRole: user?.role ?? 'BusinessOwner'
      });

      // Close modal and notify parent
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Error handling is done by the mutation hook (toast notification)
      console.error('Failed to update region:', error);
    }
  }, [validate, updateRegionMutation, region.id, formData, user?.role, onClose, onSuccess]);

  /**
   * Handle modal close
   */
  const handleClose = useCallback((): void => {
    if (!updateRegionMutation.isPending) {
      onClose();
    }
  }, [updateRegionMutation.isPending, onClose]);

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title="Edit Region"
      size="lg"
      centered
      closeOnClickOutside={!updateRegionMutation.isPending}
      closeOnEscape={!updateRegionMutation.isPending}
    >
      <Stack gap="md">
        {/* Region Name */}
        <TextInput
          label="Region Name"
          placeholder="Seattle Metro"
          value={formData['regionName'] ?? ''}
          onChange={(e) => handleChange('regionName', e.currentTarget.value)}
          error={errors['regionName']}
          required
          disabled={updateRegionMutation.isPending}
        />

        {/* Country */}
        <TextInput
          label="Country"
          placeholder="United States"
          value={formData['country'] ?? ''}
          onChange={(e) => handleChange('country', e.currentTarget.value)}
          error={errors['country']}
          required
          disabled={updateRegionMutation.isPending}
        />

        {/* State */}
        <TextInput
          label="State"
          placeholder="Washington"
          value={formData['state'] ?? ''}
          onChange={(e) => handleChange('state', e.currentTarget.value)}
          error={errors['state']}
          disabled={updateRegionMutation.isPending}
        />

        {/* City */}
        <TextInput
          label="City"
          placeholder="Seattle"
          value={formData['city'] ?? ''}
          onChange={(e) => handleChange('city', e.currentTarget.value)}
          error={errors['city']}
          disabled={updateRegionMutation.isPending}
        />

        {/* Description */}
        <Textarea
          label="Description"
          placeholder="Greater Seattle metropolitan area..."
          value={formData['description'] ?? ''}
          onChange={(e) => handleChange('description', e.currentTarget.value)}
          error={errors['description']}
          minRows={3}
          maxRows={5}
          disabled={updateRegionMutation.isPending}
        />

        {/* Active Status */}
        <Switch
          label="Active"
          description="Inactive regions are hidden from users"
          checked={formData.isActive ?? true}
          onChange={(e) => handleChange('isActive', e.currentTarget.checked)}
          disabled={updateRegionMutation.isPending}
        />

        {/* Actions */}
        <Group justify="flex-end" mt="md">
          <Button
            variant="subtle"
            leftSection={<IconX size={16} />}
            onClick={handleClose}
            disabled={updateRegionMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={handleSubmit}
            loading={updateRegionMutation.isPending}
          >
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
